import {
  type FunctionComponent,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';

import { Job, JobCompanyInfo } from '@kanda-libs/ks-frontend-services';

import { SuccessOverlayProps } from 'components/SuccessOverlay';
import { URLS } from 'config';
import { useRefreshingData } from 'hooks';
import { selectors } from 'store';
import {
  getLatestBankPaymentFromJob,
  getLatestCardPaymentFromJob,
} from 'utils';

import {
  SUCCESS_OVERLAY_CANCELLED_PROPS,
  SUCCESS_OVERLAY_LOADING_PROPS,
  SUCCESS_OVERLAY_SUCCESS_PROPS,
  SUCCESS_OVERLAY_UNKNOWN_PROPS,
} from './constants';

export interface PaymentSuccessContextType {
  id: string | undefined;
  job: Job | undefined;
  jobCompanyInfo: JobCompanyInfo | undefined;
  props: SuccessOverlayProps;
  isLoading: boolean;
}

export const PaymentSuccessContext =
  createContext<PaymentSuccessContextType | null>(null);

export const usePaymentSuccessContext = () =>
  useContext(PaymentSuccessContext) as PaymentSuccessContextType;

export interface PaymentSuccessProviderProps {
  children: ReactNode;
}

const PaymentSuccessProvider: FunctionComponent<PaymentSuccessProviderProps> =
  function ({ children }) {
    const { push } = useHistory();
    const { cleanUp } = useRefreshingData(10000);

    const job = useSelector(selectors.job.getEntity);
    const jobHasFetched = useSelector(selectors.job.getHasFetched);

    const jobCompanyInfo = useSelector(selectors.jobCompanyInfo.getEntity);
    const jobCompanyInfoHasFetched = useSelector(
      selectors.jobCompanyInfo.getHasFetched,
    );

    const payment = useMemo(() => {
      if (!job || !jobHasFetched) return undefined;
      const card = getLatestCardPaymentFromJob(job);
      const bank = getLatestBankPaymentFromJob(job);
      if (!card) return bank;
      if (!bank) return card;
      const sorted = [card, bank].sort(
        (payment1, payment2) =>
          new Date(payment2?.metadata?.created_at || 0).getTime() -
          new Date(payment1?.metadata?.created_at || 0).getTime(),
      );
      return sorted[0];
    }, [job, jobHasFetched]);

    const id = useMemo(() => job?.id, [job]);

    const link = useMemo(() => {
      if (!id) return undefined;
      return generatePath(URLS.status, { id });
    }, [id]);

    const status = useMemo(() => payment?.status, [payment]);

    const isLoading = useMemo(
      () => !jobCompanyInfoHasFetched || !jobHasFetched,
      [jobCompanyInfoHasFetched, jobHasFetched],
    );

    const paymentIsLoading = useMemo(() => {
      if (!jobCompanyInfoHasFetched || !status) return true;
      return status === 'unpaid';
    }, [jobCompanyInfoHasFetched, status]);

    const props = useMemo(() => {
      if (paymentIsLoading || !link || !status)
        return SUCCESS_OVERLAY_LOADING_PROPS;
      if (status === 'paid')
        return {
          ...SUCCESS_OVERLAY_SUCCESS_PROPS,
          link,
        };
      if (status === 'cancelled')
        return {
          ...SUCCESS_OVERLAY_CANCELLED_PROPS,
          link,
        };
      return {
        ...SUCCESS_OVERLAY_UNKNOWN_PROPS,
        link,
      };
    }, [paymentIsLoading, link, status]);

    const values = useMemo(
      () => ({
        id,
        job,
        jobCompanyInfo,
        props,
        isLoading,
      }),
      [id, job, jobCompanyInfo, props, isLoading],
    );

    const timerRef = useRef<NodeJS.Timer | undefined>();
    useEffect(() => {
      if (!status || !link) return () => {};
      if (status === 'unpaid') return () => {};
      if (status === 'cancelled') {
        cleanUp();
        timerRef.current = setTimeout(() => push(link), 3000);
        return () => {
          if (!timerRef.current) return;
          clearTimeout(timerRef.current as NodeJS.Timeout);
        };
      }
      if (status === 'paid') {
        cleanUp();
        timerRef.current = setTimeout(() => push(link), 3000);
        return () => {
          if (!timerRef.current) return;
          clearTimeout(timerRef.current as NodeJS.Timeout);
        };
      }
      return () => {};
    }, [link, status, cleanUp, push]);

    useEffect(() => () => cleanUp(), [cleanUp]);

    return (
      <PaymentSuccessContext.Provider value={values}>
        {children}
      </PaymentSuccessContext.Provider>
    );
  };

export default PaymentSuccessProvider;
